/**
 * Shared utility functions for progress tracking across different agents.
 * Provides tool call extraction, conversion, and segmentation by exchange.
 */

import {LLMRole} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {ChatSessionQuery} from 'llm/chat-library/__generated/ChatSessionQuery';
import {
  ChatEventNode,
  DisplayMessageChatEventNode,
  LLMToolCallBlock,
  LLMToolResultBlock,
} from 'llm/chat-library/messages/MessageTypes';
import {getToolDisplayName, shouldDisplayTool} from 'llm/chat-library/tool-details/types';

import {ProgressData, ProgressToolCall, ToolCallStatus} from './ProgressTypes';

export type ChatSessionNode = ExtractByTypename<ChatSessionQuery['chatSession'], 'ChatSessionNode'>;

/**
 * Maps assistant message IDs to the tool calls that occurred before that message.
 */
export interface ToolCallsByAssistantMessage {
  [assistantMessageId: string]: ProgressData;
}

/**
 * Extract text content from a tool result block.
 */
export function extractResultText(toolResult: LLMToolResultBlock): string {
  return toolResult.value.content
    .filter((content) => content.type === 'text')
    .map((content) => {
      const textBlock = content;
      if (textBlock.value.text) {
        return textBlock.value.text;
      }
      return '';
    })
    .join(' ')
    .trim();
}

/**
 * Create a ProgressToolCall from a tool call block and optional result.
 */
export function createProgressToolCall(
  toolCallBlock: LLMToolCallBlock,
  toolResultBlock: LLMToolResultBlock | null,
  createdAt: Date
): ProgressToolCall {
  const toolName = toolCallBlock.value.name;
  const resultText = toolResultBlock ? extractResultText(toolResultBlock) : undefined;
  const status = toolResultBlock ? ToolCallStatus.COMPLETED : ToolCallStatus.PENDING;

  return {
    tool: toolName,
    toolId: toolCallBlock.value.id,
    createdAt,
    status,
    displayName: getToolDisplayName(toolName, status),
    args: toolCallBlock.value.args,
    responseText: resultText,
  };
}

/**
 * Extract progress tool calls from a chat session's all threads.
 * Pairs tool calls with their corresponding results by matching IDs.
 * Tool results have the same ID as their corresponding tool call.
 */
export function getProgressToolCalls(chatSession: ChatSessionNode): Array<ProgressToolCall> {
  const toolMap = new Map<string, [LLMToolCallBlock, LLMToolResultBlock | null, Date]>();

  chatSession.allThreads.forEach((thread) => {
    thread.events.forEach((event) => {
      if (event.__typename === 'LLMMessageChatEventNode') {
        const createdAt = new Date(event.createdAt);

        event.messageBlocks?.forEach(({content: block}) => {
          if (block.type === 'tool_call') {
            toolMap.set(block.value.id, [block, null, createdAt]);
          } else if (block.type === 'tool_result') {
            const toolId = block.value.id;
            const existing = toolMap.get(toolId);
            if (existing) {
              const [toolCall, , date] = existing;
              toolMap.set(toolId, [toolCall, block, date]);
            }
          }
        });
      }
    });
  });

  return Array.from(toolMap.values())
    .map(([toolCall, toolResult, createdAt]) => {
      if (!shouldDisplayTool(toolCall.value.name)) {
        return null;
      }
      return createProgressToolCall(toolCall, toolResult, createdAt);
    })
    .filter((toolCall): toolCall is ProgressToolCall => toolCall != null)
    .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
}

/**
 * Get progress data for the current in-progress exchange only.
 * Returns tool calls that occurred after the last assistant message.
 */
export function getCurrentExchangeProgressData(
  chatSessionQuery: ChatSessionQuery,
  displayEvents: Array<ChatEventNode>
): ProgressData | null {
  if (chatSessionQuery.chatSession.__typename === 'InaccessibleInfoNode') {
    return null;
  }

  const allToolCalls = getProgressToolCalls(chatSessionQuery.chatSession);
  if (allToolCalls.length === 0) {
    return null;
  }

  const displayMessageEvents = displayEvents.filter(
    (event): event is DisplayMessageChatEventNode => event.__typename === 'DisplayMessageChatEventNode'
  );

  let lastAssistantTime: Date | null = null;
  for (let i = displayMessageEvents.length - 1; i >= 0; i--) {
    const event = displayMessageEvents[i];
    if (event.role === LLMRole.ASSISTANT) {
      lastAssistantTime = new Date(event.createdAt);
      break;
    }
  }

  const currentExchangeToolCalls = allToolCalls.filter(
    (toolCall) => lastAssistantTime == null || toolCall.createdAt > lastAssistantTime
  );

  if (currentExchangeToolCalls.length === 0) {
    return null;
  }

  return {
    toolCalls: currentExchangeToolCalls,
  };
}

/**
 * Segment tool calls by the assistant message they precede.
 * Returns a map from assistant message ID to the tool calls that happened
 * between the previous user message and that assistant message.
 */
export function getToolCallsByAssistantMessage(
  chatSessionQuery: ChatSessionQuery,
  displayEvents: Array<ChatEventNode>
): ToolCallsByAssistantMessage {
  if (chatSessionQuery.chatSession.__typename === 'InaccessibleInfoNode') {
    return {};
  }

  const allToolCalls = getProgressToolCalls(chatSessionQuery.chatSession);
  if (allToolCalls.length === 0) {
    return {};
  }

  const result: ToolCallsByAssistantMessage = {};

  const displayMessageEvents = displayEvents.filter(
    (event): event is DisplayMessageChatEventNode => event.__typename === 'DisplayMessageChatEventNode'
  );

  for (let i = 0; i < displayMessageEvents.length; i++) {
    const event = displayMessageEvents[i];
    if (event.role !== LLMRole.ASSISTANT) {
      continue;
    }

    const assistantCreatedAt = new Date(event.createdAt);

    let userCreatedAt: Date | null = null;
    for (let j = i - 1; j >= 0; j--) {
      const prevEvent = displayMessageEvents[j];
      if (prevEvent.role === LLMRole.USER) {
        userCreatedAt = new Date(prevEvent.createdAt);
        break;
      }
    }

    const toolCallsForMessage = allToolCalls.filter((toolCall) => {
      const toolCallTime = toolCall.createdAt;
      const afterUserMessage = userCreatedAt == null || toolCallTime >= userCreatedAt;
      const beforeOrAtAssistantMessage = toolCallTime <= assistantCreatedAt;
      return afterUserMessage && beforeOrAtAssistantMessage;
    });

    if (toolCallsForMessage.length > 0) {
      result[event.id] = {
        toolCalls: toolCallsForMessage,
      };
    }
  }

  return result;
}
